import { useState, type FormEvent, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useUpdateReturnCustomer,
  type ReturnRequest,
  type UpdateReturnCustomerInput,
} from "@/hooks/use-returns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { isExchangeRequest } from "@/lib/return-labels";

/** Forma za ispravku podataka kupca u kartici "Kupac". Artikli, tip, razlozi i izjava se ovdje
 *  ne mijenjaju — ostaju onakvi kakve je kupac poslao. Provjere radi server (iste kao javna forma). */
export function ReturnCustomerForm({ item, onDone }: { item: ReturnRequest; onDone: () => void }) {
  const update = useUpdateReturnCustomer();
  const [form, setForm] = useState<UpdateReturnCustomerInput>({
    firstName: item.firstName,
    lastName: item.lastName,
    email: item.email,
    phone: item.phone,
    orderNumber: item.orderNumber,
    bankAccount: item.bankAccount,
    deliveryAddress: item.deliveryAddress,
    deliveryCity: item.deliveryCity,
    deliveryPostalCode: item.deliveryPostalCode,
  });
  const isExchange = isExchangeRequest(item.requestType);
  const orderLocked = !!item.linkedOrderId;

  function set<K extends keyof UpdateReturnCustomerInput>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await update.mutateAsync({ id: item.id, ...form });
      toast.success("Podaci kupca sačuvani");
      onDone();
    } catch (err: any) {
      toast.error(err.message || "Greška pri spremanju podataka kupca");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
      <FormField label="Ime" id="rc-first-name">
        <Input id="rc-first-name" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} required />
      </FormField>
      <FormField label="Prezime" id="rc-last-name">
        <Input id="rc-last-name" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} required />
      </FormField>
      <FormField label="Email" id="rc-email">
        <Input id="rc-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
      </FormField>
      <FormField label="Telefon" id="rc-phone">
        <Input id="rc-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} required />
      </FormField>
      <FormField
        label="Broj narudžbe"
        id="rc-order-number"
        hint={orderLocked ? "Povezano s narudžbom — ukloni vezu za izmjenu." : undefined}
      >
        <Input
          id="rc-order-number"
          className="font-mono"
          value={form.orderNumber}
          onChange={(e) => set("orderNumber", e.target.value)}
          disabled={orderLocked}
        />
      </FormField>
      {item.requestType === "vraca" && (
        <FormField label="Žiro račun" id="rc-bank-account">
          <Input
            id="rc-bank-account"
            className="font-mono"
            inputMode="numeric"
            value={form.bankAccount}
            onChange={(e) => set("bankAccount", e.target.value)}
            required
          />
        </FormField>
      )}
      {isExchange && (
        <>
          <FormField label="Adresa dostave (ulica i broj)" id="rc-delivery-address" className="col-span-full">
            <Input
              id="rc-delivery-address"
              value={form.deliveryAddress}
              onChange={(e) => set("deliveryAddress", e.target.value)}
              required
            />
          </FormField>
          <FormField label="Grad dostave" id="rc-delivery-city">
            <Input id="rc-delivery-city" value={form.deliveryCity} onChange={(e) => set("deliveryCity", e.target.value)} required />
          </FormField>
          <FormField label="Poštanski broj dostave" id="rc-delivery-postal">
            <Input
              id="rc-delivery-postal"
              value={form.deliveryPostalCode}
              onChange={(e) => set("deliveryPostalCode", e.target.value)}
              required
            />
          </FormField>
        </>
      )}
      <div className="col-span-full flex justify-end gap-2 pt-1">
        <Button type="button" size="sm" variant="ghost" onClick={onDone} disabled={update.isPending}>
          Odustani
        </Button>
        <Button type="submit" size="sm" disabled={update.isPending}>
          {update.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
          Sačuvaj
        </Button>
      </div>
    </form>
  );
}

function FormField({
  label,
  id,
  hint,
  className,
  children,
}: {
  label: string;
  id: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ""}`}>
      <label htmlFor={id} className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </label>
      {children}
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </div>
  );
}
